import React, { useCallback, useMemo, useRef, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Skeleton, Space, Tag } from 'antd';
import type { TableColumnsType } from 'antd';

import { BoxOtaHistoricalSteps } from './BoxOtaHistoricalSteps';
import { BoxOtaStepCounter } from './BoxOtaStepCounter';
import {
  IOtaHistoricalExecution,
  getOtaExecutionHistory,
} from '../api/getOtaExecutionHistory';
import { OTA_EXECUTION_STATUS, OtaExecutionStatus } from '../types';
import StyledTable from '@/components/tables/StyledTable';
import { formatDuration, formatISODate } from '@/helpers/dates';
import { ElapsedTimer } from '@/shared/components/ElapsedTimer';

interface IBoxOtaHistoryProps {
  lpUI: string;
  isOtaInProgress?: boolean;
  onRefresh: () => void;
}

type ISkeletonRow = { _skeleton: true; key: string };
type ITableRow = IOtaHistoricalExecution | ISkeletonRow;

const isSkeletonRow = (row: ITableRow): row is ISkeletonRow =>
  '_skeleton' in row;

const buildSkeletonRows = (count: number): ISkeletonRow[] =>
  Array.from({ length: count }, (_, i) => ({
    _skeleton: true as const,
    key: `skeleton-${i}`,
  }));

const SKELETON_CELL = <Skeleton.Input active size="small" block />;

const skeletonRender =
  <T = unknown,>(
    fn?: (val: T, record: IOtaHistoricalExecution) => React.ReactNode
  ) =>
  // eslint-disable-next-line react/display-name -- Reason: render helper for table columns, not a React component
  (val: T, record: ITableRow): React.ReactNode => {
    if (isSkeletonRow(record)) return SKELETON_CELL;
    if (fn) return fn(val, record as IOtaHistoricalExecution);

    return val as React.ReactNode;
  };

const STATUS_TAG: Record<OtaExecutionStatus, { color: string; label: string }> =
  {
    [OTA_EXECUTION_STATUS.CANCELLED]: { color: 'warning', label: 'Cancelled' },
    [OTA_EXECUTION_STATUS.COMPLETED]: { color: 'success', label: 'Completed' },
    [OTA_EXECUTION_STATUS.FAILED]: { color: 'error', label: 'Failed' },
    [OTA_EXECUTION_STATUS.IN_PROGRESS]: {
      color: 'processing',
      label: 'In Progress',
    },
    [OTA_EXECUTION_STATUS.UNSPECIFIED]: { color: 'default', label: 'Unknown' },
  };

const buildColumns = (
  expandedKeys: Set<string>
): TableColumnsType<ITableRow> => [
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: skeletonRender<OtaExecutionStatus>((status, record) => {
      const { color, label } =
        STATUS_TAG[status] ?? STATUS_TAG[OTA_EXECUTION_STATUS.UNSPECIFIED];
      const rowKey = `${record.id}-${record.started_at}`;
      const isExpanded = expandedKeys.has(rowKey);

      return (
        <Space size="small" align="center">
          <Tag color={color} data-testid="ota-history-status-tag">
            {label}
          </Tag>
          {status === OTA_EXECUTION_STATUS.IN_PROGRESS && (
            <BoxOtaStepCounter steps={record.steps} isExpanded={isExpanded} />
          )}
        </Space>
      );
    }),
  },
  {
    title: 'Firmware Version',
    dataIndex: 'firmware_version',
    key: 'firmware_version',
    render: skeletonRender(),
  },
  {
    title: 'Started At',
    dataIndex: 'started_at',
    key: 'started_at',
    render: skeletonRender<string>((value) => formatISODate(value)),
  },
  {
    title: 'Duration',
    dataIndex: 'duration_seconds',
    key: 'duration_seconds',
    render: skeletonRender<number>((value, record) => {
      if (record.status === OTA_EXECUTION_STATUS.IN_PROGRESS) {
        return <ElapsedTimer startedAt={record.started_at} />;
      }

      return formatDuration(value);
    }),
  },
  {
    title: 'Restarts',
    dataIndex: 'restart_count',
    key: 'restart_count',
    render: skeletonRender(),
  },
  {
    title: 'Last Job ID',
    dataIndex: 'last_job_id',
    key: 'last_job_id',
    render: skeletonRender(),
  },
];

export const BoxOtaHistory = ({
  lpUI,
  isOtaInProgress = false,
  onRefresh,
}: IBoxOtaHistoryProps) => {
  // Reason: page et pageSize sont des paramètres de getOtaExecutionHistory ci-dessous.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading } = getOtaExecutionHistory({
    lpUI,
    listRequest: { page_size: pageSize, page_offset: (page - 1) * pageSize },
    config: { refetchInterval: isOtaInProgress ? 5_000 : false },
  });

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const lastTotalSize = useRef<number | undefined>(undefined);
  if (data?.total_size !== undefined) {
    lastTotalSize.current = data.total_size;
  }

  const columns = useMemo(
    () => buildColumns(new Set(expandedRowKeys)),
    [expandedRowKeys]
  );

  const executions = useMemo(() => data?.executions ?? [], [data?.executions]);

  const tableData = useMemo<ITableRow[]>(
    () => (isLoading ? buildSkeletonRows(pageSize) : executions),
    [isLoading, executions, pageSize]
  );

  const handlePageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      setPage(newPage);
      setPageSize(newPageSize);
    },
    []
  );

  const handleExpand = useCallback(
    (expanded: boolean, record: IOtaHistoricalExecution) => {
      const key = `${record.id}-${record.started_at}`;
      setExpandedRowKeys((prev) =>
        expanded ? [...prev, key] : prev.filter((k) => k !== key)
      );
    },
    []
  );

  return (
    <Card
      size="small"
      title="History"
      extra={
        <Button
          icon={<ReloadOutlined />}
          size="small"
          onClick={onRefresh}
          data-testid="ota-history-refresh-button"
        />
      }
    >
      <StyledTable
        rowKey={(record: ITableRow) =>
          isSkeletonRow(record)
            ? record.key
            : `${record.id}-${record.started_at}`
        }
        dataSource={tableData}
        columns={columns}
        loading={false}
        size="small"
        pagination={{
          current: page,
          pageSize,
          total: lastTotalSize.current,
          onChange: handlePageChange,
          showSizeChanger: false,
        }}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded: boolean, record: ITableRow) => {
            if (!isSkeletonRow(record)) {
              handleExpand(expanded, record);
            }
          },
          expandedRowRender: (record: ITableRow) => {
            if (isSkeletonRow(record)) return null;

            return (
              <BoxOtaHistoricalSteps
                steps={record.steps}
                executionStatus={record.status}
              />
            );
          },
          rowExpandable: (record: ITableRow) =>
            !isSkeletonRow(record) && record.steps.length > 0,
        }}
        data-testid="ota-history-table"
      />
    </Card>
  );
};
